import AddIcon from '@mui/icons-material/Add';
import { Box, Button, TextField, Tooltip, Typography } from '@mui/material';
import { LINK, MEDIALINK, TITLE } from '@opengov/stories-shared/constants';
import { IPublicationFooterSettings } from '@opengov/stories-shared/types';
import React, { useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { IOnlinePublicationMessages } from '../../../../../types';

import { MAX_MEDIA_LINKS_COUNT } from '../../../../constants';

import { ContrastColorsSettings } from '../../../../common/components/ContrastColorsSettings';
import { getFacebookLink, getMediaLinks, getXLink } from '../../../../utils/helper';

import { MediaLink } from '../MediaLink/MediaLink';

import { styles } from './Footer.styles';

export type IOnlinePublicationFooterProps = {
  publicationFooterSettings: IPublicationFooterSettings;
  messages: IOnlinePublicationMessages;
  handleUpdateFooterSettings(payload: Partial<IPublicationFooterSettings>): void;
};

export const Footer = (props: IOnlinePublicationFooterProps) => {
  const { publicationFooterSettings, messages, handleUpdateFooterSettings } = props;

  const [mediaLinksArray, setMediaLinksArray] = useState(getMediaLinks(publicationFooterSettings?.mediaLinks));

  const [facebookURL, setFacebookURL] = useState(getFacebookLink(publicationFooterSettings?.mediaLinks).link);
  const [xURL, setXURL] = useState(getXLink(publicationFooterSettings?.mediaLinks).link);
  const [facebookLink, setFacebookLink] = useState(getFacebookLink(publicationFooterSettings?.mediaLinks));
  const [xLink, setXLink] = useState(getXLink(publicationFooterSettings?.mediaLinks));

  useEffect(() => {
    setMediaLinksArray(getMediaLinks(publicationFooterSettings?.mediaLinks));
    setFacebookURL(getFacebookLink(publicationFooterSettings?.mediaLinks).link);
    setXURL(getXLink(publicationFooterSettings?.mediaLinks).link);
    setFacebookLink(getFacebookLink(publicationFooterSettings?.mediaLinks));
    setXLink(getXLink(publicationFooterSettings?.mediaLinks));
  }, [publicationFooterSettings]);

  const updateMedialinks = (mediaId?: string, value?: string, type?: string) => {
    const { mediaLinks } = publicationFooterSettings;
    if (type === TITLE) {
      mediaLinks.find((mediaLink) => mediaLink.id === mediaId).title = value;
    } else {
      mediaLinks.find((mediaLink) => mediaLink.id === mediaId).link = value;
    }
    handleUpdateFooterSettings({ mediaLinks: mediaLinks });
  };

  const addLinkToMedia = () => {
    const { mediaLinks } = publicationFooterSettings;
    if (mediaLinksArray.length < 3) {
      mediaLinks.push({ id: uuidv4(), link: '', type: MEDIALINK, title: '' });
      handleUpdateFooterSettings({ mediaLinks: mediaLinks });
    }
  };

  const removeLinkFromMedia = (id) => {
    const updatedMediaLinks = publicationFooterSettings.mediaLinks.filter((mediaLink) => mediaLink.id !== id);
    handleUpdateFooterSettings({
      mediaLinks: updatedMediaLinks
    });
  };

  const handleChangeFacebookLink = (value) => {
    setFacebookURL(value);
  };

  const handleChangeXLink = (value) => {
    setXURL(value);
  };

  return (
    <Box sx={styles.container} data-test="footer">
      <div>
        <Box component="section">
          <Typography variant="subtitle1" sx={styles.styleHeader}>
            {messages.style}
          </Typography>
        </Box>
        <Box component="section">
          <Typography variant="body2" sx={styles.styleDescription}>{messages.styleDescription}</Typography>
        </Box>
        <Box component="section" sx={styles.mt24}>
          <ContrastColorsSettings
            dataTest="data-footer-colors-settings"
            primaryColorTitle={messages.backgroundColor}
            primaryColors={publicationFooterSettings?.backgroundColors || []}
            secondaryColorTitle={messages.fontColor}
            secondaryColors={publicationFooterSettings?.fontColors || []}
            onChangePrimaryColor={(colorOptions) => handleUpdateFooterSettings({ backgroundColors: colorOptions })}
            onChangeSecondaryColor={(colorOptions) => handleUpdateFooterSettings({ fontColors: colorOptions })}
          />
        </Box>
        <Box component="section" sx={styles.mt40}>
          <Typography variant="subtitle1" sx={styles.styleHeader}>
            {messages.websiteAndSocialMedia}
          </Typography>
        </Box>
        <Box component="section" sx={styles.mb24}>
          <Typography variant="body2" sx={styles.styleDescription}>{messages.websiteAndSocialMediaDescription}</Typography>
        </Box>

        {mediaLinksArray.map((mediaLink, mediaLinkPosition) => {
          return (
            <span key={mediaLink.id}>
              <MediaLink
                mediaLink={mediaLink}
                mediaLinkPosition={mediaLinkPosition}
                messages={messages}
                updateMedialinks={updateMedialinks}
                removeLinkFromMedia={removeLinkFromMedia}
              />
            </span>
          );
        })}

        {mediaLinksArray.length < MAX_MEDIA_LINKS_COUNT ? (
          <Button
            variant="outlined"
            size="small"
            onClick={addLinkToMedia}
            startIcon={<AddIcon />}
            sx={styles.btn}
            data-test="add-link"
          >
            {messages.addLink}
          </Button>
        ) : (
          <Tooltip title={messages.maxMediaURL} placement="bottom-end">
            <span>
              <Button
                variant="outlined"
                size="small"
                disabled={true}
                startIcon={<AddIcon />}
                sx={styles.btn}
                data-test="add-link"
              >
                {messages.addLink}
              </Button>
            </span>
          </Tooltip>
        )}

        <Box sx={styles.mt24}>
          <TextField
            label={messages.facebookURL}
            size="small"
            fullWidth
            placeholder={messages.facebookURLDescription}
            value={facebookURL}
            onChange={(e) => handleChangeFacebookLink(e.target.value)}
            onBlur={(e) => updateMedialinks(facebookLink.id, e.target.value, LINK)}
            inputProps={{ 'data-test': 'facebook', className: 'no-format' }}
          />
        </Box>

        <Box sx={styles.mt16}>
          <TextField
            label={messages.xURL}
            size="small"
            fullWidth
            placeholder={messages.xURLDescription}
            value={xURL}
            onChange={(e) => handleChangeXLink(e.target.value)}
            onBlur={(e) => updateMedialinks(xLink.id, e.target.value, LINK)}
            inputProps={{ 'data-test': 'x-url-input', className: 'no-format' }}
          />
        </Box>
      </div>
    </Box>
  );
};
